<template>
  <div>
    <v-container
      class="hidden-md-and-up" 
      fluid 
    >
      <v-row>
        <v-col 
          v-for="rank in sortedRanks"
          :key="rank.id"
          cols="12"
          md="6"
          xl="4"
        >
          <v-card class="elevation-5 pa-2">
            <v-container
              class="pa-2" 
              fluid
            >            
              <v-row>
                <v-col
                  class="pt-0" 
                  cols="5"
                >
                  <helper-image-input  
                    v-model="images[rank.id]"              
                    :max-size-in-mb="2"
                    data-vv-name="image"
                    field-name="image"
                    accept-type=".svg"            
                  >  
                    <v-card-title 
                      slot="activator"
                      primary-title 
                    >
                      <v-img
                        :src="images[rank.id] ? images[rank.id].imageURL : rank.image_url"
                        height="100px"
                        contain
                      />          
                    </v-card-title>                            
                  </helper-image-input>
                  <div 
                    v-if="images[rank.id]"
                    class="text-center"
                  >         
                    <v-btn 
                      :disabled="saving"
                      text
                      icon
                      @click="resetImageHolder(rank.id)"
                    >
                      <v-icon size="30">
                        mdi-close-circle-outline
                      </v-icon>
                    </v-btn>     
                    <v-btn 
                      :loading="saving"
                      text
                      icon
                      color="primary"
                      @click="uploadImage(rank.id)"
                    >
                      <v-icon size="30">
                        mdi-cloud-upload
                      </v-icon>
                    </v-btn>
                  </div>
                </v-col>
                <v-col
                  cols="7"
                >
                  <v-card-title primary-title>
                    <span class="headline text-capitalize">
                      {{ `Rank ${rank.value}` }}                      
                    </span>   
                  </v-card-title>
                  <v-card-subtitle>
                    <span class="text-capitalize">
                      {{ rank.title }}
                    </span>
                  </v-card-subtitle>
                </v-col>
              </v-row>
            </v-container>
            <v-divider light />
            <v-card-actions>
              <v-spacer />
              <div>
                <setup-ranks-dialog-edit
                  :id="rank.id"
                  :title="rank.title"
                  @updated="loadRanks"
                />
                <setup-ranks-dialog-delete
                  :rank="rank"
                  @deleted="loadRanks"
                />
              </div>          
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card class="elevation-10 hidden-sm-and-down">
            <v-toolbar
              flat
              color="white"
            >
              <v-toolbar-title> Ranks </v-toolbar-title>
              <v-spacer />            
              <setup-ranks-dialog-create 
                class="mx-1" 
                @created="created"
              />
            </v-toolbar>
            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="ranks"
              :sort-by.sync="sortBy"
              :items-per-page="itemsPerPage"
              item-key="id"
            >
              <template v-slot:item="props">
                <tr>
                  <td>{{ props.item.value }}</td>
                  <td class="pa-2"> 
                    <helper-image-input  
                      v-model="images[props.item.id]"              
                      :max-size-in-mb="2"
                      data-vv-name="image"
                      field-name="image"
                      accept-type=".svg"    
                      extension="ext:svg"          
                    >  
                      <v-avatar 
                        slot="activator"
                        class="cursor-pointer"
                        tile
                      >
                        <img
                          :src="images[props.item.id] ? images[props.item.id].imageURL : props.item.image_url"
                        >       
                      </v-avatar>                            
                    </helper-image-input>       
                    <template 
                      v-if="images[props.item.id]"
                    >         
                      <v-btn 
                        :disabled="saving"
                        text
                        icon
                        @click="resetImageHolder(props.item.id)"
                      >
                        <v-icon size="30">
                          mdi-close-circle-outline
                        </v-icon>
                      </v-btn>     
                      <v-btn 
                        :loading="saving"
                        text
                        icon
                        color="primary"
                        @click="uploadImage(props.item.id)"
                      >
                        <v-icon size="30">
                          mdi-cloud-upload
                        </v-icon>
                      </v-btn>
                    </template>             
                  </td>                  
                  <td>{{ props.item.title }}</td>
                  <td>
                    <setup-ranks-dialog-edit
                      :id="props.item.id"
                      :title="props.item.title"
                      @updated="loadRanks"
                    />
                    <span class="mx-1" />
                    <setup-ranks-dialog-delete
                      :rank="props.item"
                      @deleted="loadRanks"
                    />   
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  export default {
    components: {
      HelperImageInput: () => import('@/components/helper/ImageInput'),
      SetupRanksDialogCreate: () => import('@/components/setup/ranks/DialogCreate'),
      SetupRanksDialogEdit: () => import('@/components/setup/ranks/DialogEdit'),
      SetupRanksDialogDelete: () => import('@/components/setup/ranks/DialogDelete'),
    },
    metaInfo () {
      return {
        title: 'Setup - Ranks',
      }
    },
    data () {
      return {
        headers: [
          {
            text: 'Rank',
            align: 'left',
            value: 'value',
          },
          {
            text: 'Logo',
            align: 'left',
            sortable: false,
            value: '',
          },
          {
            text: 'Title',
            align: 'left',
            value: 'title',
          },
          {
            text: 'Actions',
            sortable: false,
            value: '',
          },
        ],
        sortBy: 'value',
        itemsPerPage: 10,
        ranks: [],
        images: [],
        loading: false,
        saving: false,
        operators: {
          'asc': (rankA, rankB) => {
            return rankA - rankB 
          },
          'desc': (rankA, rankB) => {
            return rankB - rankA 
          },
        },
        orderBy: 'asc',
      }
    },
    computed: {
      sortedRanks () {
        return [...this.ranks].sort((a, b) => this.operators[this.orderBy](a.value, b.value))
      },
    },
    created () {
      this.loadRanks()
    },
    methods: {
      savedImage (id) {     
        this.$api.ranks().list()
          .then(response => {
            this.ranks = response.data

            this.images[id] = null
          })      

        this.$snack({
          message: 'Photo uploaded successfully!',
        })
      },
      loadRanks () {
        this.loading = true
      
        this.$api.ranks().list()
          .then(response => {
            this.ranks = response.data

            this.loading = false
          })
      },
      uploadImage (id) {
        this.saving = true

        this.$api.ranks().uploadImage(id, this.images[id].formData)
          .then(response => {
            if (response.status >= 200 && response.status < 300) {
              this.savedImage(id)
            }
          })
          .catch(() => {
            this.$snack({
              color: 'error',
              message: 'Photo upload failed, please try again',
            })
          }) 
          .then(() => {
            this.saving = false
          })
      },
      created (rank) {
        this.ranks.unshift(rank)
      },
      resetImageHolder (id) {
        this.$set(this.images, id, null)
      },
    },
  }
</script>

<style scoped>
  ::v-deep .v-toolbar__content{
    margin-left:0px !important;
  }

  ::v-deep .v-table__overflow{
    overflow-x: hidden;
  }

  .cursor-pointer{
    cursor: pointer;
  }
</style>
